/**
 * The "agent is working on…" summary the proposal chat surfaces show while a
 * turn runs. Shared by `useProposalPolling` (compose) and
 * `useProposalChatProgress` (the proposal panel) so both read the progress
 * payload the same way.
 */

/** Lightweight summary of the most recent reasoning step. */
export interface ReasoningStepSummary {
  stage: string;
  content: string;
  step_number: number;
  is_final: boolean;
}

interface ProgressStep {
  stage?: unknown;
  content?: unknown;
  step_number?: unknown;
  is_final?: unknown;
}

/** The part of a progress-poll payload the summary is read from. */
export interface PartialStepsPayload {
  partial_steps?: ProgressStep[];
}

/**
 * The latest step of a progress payload, or undefined when the payload has no
 * steps or the latest one has no text content yet. Tolerates loosely-typed
 * payloads — every field is checked before it is trusted.
 */
export function extractLatestStepSummary(
  data: PartialStepsPayload
): ReasoningStepSummary | undefined {
  const partialSteps = data.partial_steps;
  if (!Array.isArray(partialSteps) || partialSteps.length === 0) {
    return undefined;
  }
  // Unlike `resolveLatestStepTitle` in `latestReasoningStep.ts`, which
  // excludes the final step, this intentionally includes it: the proposal
  // surface shows the final content once a turn completes.
  const last = partialSteps[partialSteps.length - 1];
  // Reject empty/whitespace-only content: a processing step with no text yet
  // must not replace the previous non-empty summary with a blank one. Returning
  // undefined keeps the existing summary visible until the step has text.
  if (!last || typeof last.content !== 'string' || last.content.trim() === '') {
    return undefined;
  }
  return {
    stage: typeof last.stage === 'string' ? last.stage : '',
    content: last.content,
    step_number: typeof last.step_number === 'number' ? last.step_number : 0,
    is_final: !!last.is_final,
  };
}

/** Whether two summaries render identically (state-setter bail-out). */
export function isSameStepSummary(
  a: ReasoningStepSummary | null,
  b: ReasoningStepSummary
): boolean {
  return (
    a !== null &&
    a.step_number === b.step_number &&
    a.content === b.content &&
    a.stage === b.stage &&
    a.is_final === b.is_final
  );
}
